import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { EnvelopeSimple, Copy, Check, XCircle } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusVariant = {
  pending: 'default',
  used: 'outline',
  revoked: 'destructive',
  expired: 'secondary',
};

const InviteManagement = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', display_name: '' });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/invites`, { withCredentials: true });
      setInvites(data);
    } catch (e) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/invites`, form, { withCredentials: true });
      setCreatedInvite(data);
      setForm({ email: '', name: '', display_name: '' });
      loadInvites();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleRevoke = async (inviteId) => {
    if (!window.confirm('Revoke this invitation? The link will no longer work.')) return;
    try {
      await axios.post(`${API_URL}/api/admin/invites/${inviteId}/revoke`, {}, { withCredentials: true });
      loadInvites();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard denied
    }
  };

  if (loading) return <div className="text-center py-8">Loading invites...</div>;

  // Counted from the list itself, using the server's own status values, so
  // the summary can never say something the rows below do not.
  const counts = ['pending', 'used', 'expired', 'revoked']
    .map((st) => [st, invites.filter((i) => i.status === st).length]);

  return (
    <div className="space-y-4" data-testid="invite-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-semibold">Onboarding Invites</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Single-use, 48-hour expiring links. Users set their own passwords.
          </p>
          <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="invite-summary">
            {counts.map(([st, n]) => (
              <span key={st}><span className="tabular-nums text-foreground">{n}</span> {st}</span>
            ))}
          </p>
        </div>

        <Dialog
          open={showCreate || !!createdInvite}
          onOpenChange={(open) => {
            if (!open) {
              setShowCreate(false);
              setCreatedInvite(null);
            } else {
              setShowCreate(true);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-invite-button">
              <EnvelopeSimple className="w-4 h-4 mr-2" />
              Create invite
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {createdInvite ? 'Invitation Created' : 'Create Invitation'}
              </DialogTitle>
              <DialogDescription>
                {createdInvite
                  ? 'Copy this link now. It will not be shown again.'
                  : 'The user will set their own password using a one-time link.'}
              </DialogDescription>
            </DialogHeader>

            {!createdInvite && (
              <form onSubmit={handleCreate} className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="inv-email">Email *</Label>
                  <Input
                    id="inv-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="invite-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-name">Name *</Label>
                  <Input
                    id="inv-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="invite-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inv-display">Display Name</Label>
                  <Input
                    id="inv-display"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    data-testid="invite-display-name-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="submit-invite"
                >
                  Generate Link
                </Button>
              </form>
            )}

            {createdInvite && (
              <div className="space-y-4 pt-2" data-testid="invite-link-display">
                <div className="p-3 bg-muted border border-border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">One-time link (copy now):</p>
                  <p className="text-xs font-mono break-all text-foreground" data-testid="invite-url">
                    {createdInvite.onboarding_url}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyLink(createdInvite.onboarding_url)}
                    className="flex-1 bg-primary hover:bg-primary/90"
                    data-testid="copy-invite-link"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4 mr-2" weight="bold" /> Copied</>
                    ) : (
                      <><Copy className="w-4 h-4 mr-2" /> Copy Link</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setCreatedInvite(null); setShowCreate(false); }}
                  >
                    Done
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with the user through a trusted channel. It expires in 48 hours and
                  can only be used once.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No invitations yet
                </TableCell>
              </TableRow>
            )}
            {invites.map((inv) => (
              <TableRow key={inv.id} data-testid={`invite-row-${inv.id}`}>
                <TableCell className="font-mono text-sm">{inv.email}</TableCell>
                <TableCell>{inv.display_name || inv.name}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant[inv.status] || 'secondary'}>
                    {inv.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}
                </TableCell>
                <TableCell className="text-right">
                  {inv.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(inv.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`revoke-invite-${inv.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default InviteManagement;
